import { FC, useEffect, useState, ReactNode } from 'react';
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from "react-hook-form";
import Select from 'react-select';

import toast from 'react-hot-toast';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import './ChartAccountModal.css'

import { CAFormValidate } from '../../../resources/form-validator';
import { CA_REPORT, CA_ACC_TYPE } from '../../../resources/constants';
import closeBtn from '../../../assets/images/close-btn.svg';
import { CreateChartAccountFormI } from '../../../resources/form-props';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { getBranch } from '../../../store/user/selectors'

// Placeholder shown in the read-only Report/Account Type fields until the real value is known -
// avoids defaulting the display to CA_REPORT[0]/CA_ACC_TYPE[0] (e.g. "Debit"), which would show
// a value that hasn't actually been resolved yet.
const UNRESOLVED_DETAIL = [{ label: '', value: '' }];

interface Props {
    closeModal: (result?: { id?: number, name?: string, account?: string, description?: string, updated?: boolean, metadataOnly?: boolean, accountOption?: any }) => void,
    addRecord: boolean,
    record?: any,
    sshSection?: { start_range: number, end_range: number, name: string } | null,
    parentAccountIn?: number[] | null,
    prefillName?: string | null,
    preselectFirstAccount?: boolean,
    metadataOnly?: boolean,
    metadataAccountOptions?: any[],
    forcedParentId?: number | null,
    usedAccountNumbers?: number[]
}

interface Props1 {
    options: any,
    name: any
}
const RecursiveSelect: FC<Props1> = ({ options, name }) => {
    //console.log(options);

    return (

        options.map((option: any) => (

            <>
                <option value={option.id}>{option.description}</option>
                {option.child && (option.child.length > 0) && (
                    <RecursiveSelect options={option.child} name={name} />
                )}
            </>
        ))

    );
};
const ChartAccountModal: FC<Props> = ({ closeModal, addRecord, record = null, sshSection = null, parentAccountIn = null, prefillName = null, preselectFirstAccount = false, metadataOnly = false, metadataAccountOptions = [], forcedParentId = null, usedAccountNumbers = [] }) => {
    const { t } = useTranslation('components.Modal.ChartOfAccount.chartAccountModal');
    // Labels are display-only; the API only ever receives .value ('D'/'C').
    const translateAccType = (option: any) => option && { ...option, label: t(`accountType.${option.value}`, option.label) };
    const { chartAvailableAccounts, createChart, updateChart, getAccountLebel } = useChartAccountApi();

    const branchData = useSelector(getBranch)

    const { control, register, watch, handleSubmit, getValues, resetField, reset, setValue, formState: { errors } } = useForm<CreateChartAccountFormI>({
        defaultValues: {
            //groupHeader: null,
            //subHeader: null,
            //parent_id: null,
            id: null,
            revrex_account_id: null,
            account: "",
            report_code: "VP",
            parenthisys_control: null,
            financial_statement: null,
            debit: '+',
            credit: '-',
            name: '',
            description: ''
        },
        resolver: metadataOnly ? undefined : yupResolver(CAFormValidate)
    });

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showPageLoader, setShowPageLoader] = useState<boolean>(false);
    const [isAccountTypeLoading, setIsAccountTypeLoading] = useState<boolean>(false);
    const [accountNumbers, setAccountNumbers] = useState<any[]>([]);
    const [accountDetails, setAccountDetails] = useState<any>({
        label: null,
        accountType: UNRESOLVED_DETAIL,
        report: UNRESOLVED_DETAIL
    });

    useEffect(() => {
        if (!metadataOnly) fetchAvailableAccounts();
    }, [])

    useEffect(() => {
        if (record) {
            if (metadataOnly) {
                reset({
                    ...getValues(),
                    id: record.id || null,
                    account: record.accountOption || (record.account ? { label: record.account, value: record.account } : null),
                    name: record.name || '',
                    description: record.description || ''
                });
                return;
            }
            const selectedReport = CA_REPORT.filter(e => e['value'] == record['financial_statement']);
            const selectedAccType = CA_ACC_TYPE.filter(e => e['value'] == record['parenthisys_control'])

            if (record['account']) {
                setIsAccountTypeLoading(true);
                fetchAccountLabel(record['account'])
            }

            reset({
                id: record['id'],
                revrex_account_id: record['revrex_account_id'],
                account: (record['account'] && {
                    label: record['account'],
                    value: record['account']
                }) || null,
                report_code: record['report_code'] || 'VP',
                parenthisys_control: translateAccType((selectedAccType.length && selectedAccType[0]) || CA_ACC_TYPE[0]),
                financial_statement: (selectedReport.length && selectedReport[0]) || CA_REPORT[0],
                debit: record['debit'],
                credit: record['credit'],
                name: record['name'],
                description: record['description']
            })
        }
    }, [record])

    // Prefill the account name with the ERP account name (used by COA Classification create flow)
    useEffect(() => {
        if (addRecord && prefillName) {
            setValue("name", prefillName);
        }
    }, [prefillName, addRecord])

    // Preselect the first available account once the account list is loaded
    useEffect(() => {
        if (!preselectFirstAccount || !addRecord || getValues('account')) {
            return;
        }
        const firstGroup = accountNumbers.find((group: any) => group?.options?.length > 0);
        const firstOption = firstGroup?.options?.[0];
        if (firstOption) {
            setValue("account", firstOption);
            setIsAccountTypeLoading(true);
            fetchAccountLabel(firstOption.value, !!prefillName);
        }
    }, [accountNumbers, preselectFirstAccount, addRecord])

    const fetchAvailableAccounts = (): Promise<any[]> => {
        return new Promise<any[]>((resolve, reject) => {
            const params: any = {
                branch_id: +branchData['id'],
                available_account: addRecord ? false : true
            };
            if (sshSection) {
                params.start_range = sshSection.start_range;
                params.end_range = sshSection.end_range;
            }
            if (parentAccountIn && (parentAccountIn.length > 0)) {
                params.parent_account_in = parentAccountIn.join(',');
            }
            chartAvailableAccounts(params, (message: string, resp: any) => {
                let data = resp.data.data.map((group: any) => ({
                    label: group.section,
                    options: group.available.map((num: any) => ({
                        label: `${num}`,
                        value: num,
                        parent_id: forcedParentId || group.section_id
                    }))
                }));
                if (sshSection && !data.some((group: any) => group?.options?.length > 0)) {
                    const used = new Set(usedAccountNumbers.map(Number));
                    const options = Array.from(
                        { length: Math.max(0, (sshSection.end_range - sshSection.start_range) + 1) },
                        (_, i) => sshSection.start_range + i
                    ).filter(num => !used.has(num)).map(num => ({
                        label: `${num}`,
                        value: num,
                        parent_id: forcedParentId || null
                    }));
                    data = options.length ? [{ label: sshSection.name, options }] : data;
                }
                setAccountNumbers(data);
                resolve(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                resolve([]);
            })

        })
    }

    const fetchAccountLabel = (account: any, skipNameOverride: boolean = false): Promise<any[]> => {
        return new Promise<any[]>((resolve, reject) => {
            const params = {
                account: account,
                branch_id: +branchData['id']
            };

            getAccountLebel(params, (message: string, resp: any) => {
                if (resp.data.data) {
                    const label = `${resp.data.data.label} > ${account}`;
                    const accounType = CA_ACC_TYPE.filter((e: any) => e['value'] === resp.data.data['account_type']);
                    const fStatement = CA_REPORT.filter((e: any) => e['value'] === resp.data.data['report_code']);
                    // const report_code = resp.data.data['account_chart']['report_code'];

                    if (fStatement.length) {
                        setValue("financial_statement", (fStatement && fStatement[0]) || null)
                    }
                    if (accounType.length) {
                        setValue("parenthisys_control", translateAccType((accounType && accounType[0]) || null))
                    } else {
                        toast.error(t('error.unmappedAccountType'));
                    }
                    if ( !skipNameOverride && resp.data.data['account_chart'] && resp.data.data['account_chart']['name'] ) {
                        setValue("name", resp.data.data['account_chart']['name'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['description'] ) {
                        setValue("description", resp.data.data['account_chart']['description'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['revrex_account_id'] ) {
                        setValue("revrex_account_id", resp.data.data['account_chart']['revrex_account_id'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['debit'] ) {
                        setValue("debit", resp.data.data['account_chart']['debit'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['credit'] ) {
                        setValue("credit", resp.data.data['account_chart']['credit'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['id'] ) {
                        setValue("id", resp.data.data['account_chart']['id'])
                    }
                    if ( resp.data.data['account_chart'] && resp.data.data['account_chart']['report_code'] && !forcedParentId ) {
                        setValue("report_code", resp.data.data['account_chart']['report_code'])
                    }

                    setAccountDetails({
                        label,
                        accountType: accounType.length ?  accounType : UNRESOLVED_DETAIL,
                        report: fStatement.length ? fStatement : UNRESOLVED_DETAIL

                    });
                }

                setIsAccountTypeLoading(false);
                // setGHList(resp.data.data.result);
                // return resolve(resp.data.data.available_list);
            }, (message: string, resp: any) => {
                setIsAccountTypeLoading(false);
                toast.error(message);
                // return resolve([])
            })
        })
    }

    const processData = (values: any) => {
        if (metadataOnly) {
            closeModal({
                id: record?.id,
                account: values.account?.accountNum || record?.account,
                accountOption: values.account,
                name: record?.name,
                description: values.description || '',
                metadataOnly: true
            });
            return;
        }

        if (isAccountTypeLoading || !values['parenthisys_control']) {
            toast.error(t('error.accountTypeLoading'));
            return;
        }

        const params: any = {
            account: (values['account'] && +values['account']['value']) || null,
            report_code: forcedParentId ? 'VP' : values['report_code'],
            parenthisys_control: (values['parenthisys_control'] && values['parenthisys_control']['value']) || null,
            financial_statement: (values['financial_statement'] && values['financial_statement']['value']) || null,
            name: values['name'],
            description: values['description'],
            branch_id: +branchData['id']
        }

        if ( !addRecord ) {
            setShowLoader(true);
            params['id'] = values['id'];
            updateChart(params, (message: string, resp: any) => {
                setShowLoader(false);
                toast.success(message)
                _handleCloseModal({ updated: true });
                // setGHList(resp.data.data.result);
                // return resolve(resp.data.data.result);
            }, (message: string, resp: any) => {
                setShowLoader(false);
                toast.error(message);
                // return resolve([])
            })
        } else {
            setShowLoader(true);
            params['parent_id'] = forcedParentId || (values['account'] && values['account']['parent_id']) || null;
            //console.log(params);
            createChart(params, (message: string, resp: any) => {
                setShowLoader(false);
                toast.success(message)
                const created = resp?.data?.data;
                if (created && created.id) {
                    reset();
                    closeModal({ id: created.id, name: created.name, account: created.account });
                } else {
                    _handleCloseModal();
                }
            }, (message: string, resp: any) => {
                setShowLoader(false);
                toast.error(message);
                // return resolve([])
            })
        }
    }

    const _handleCloseModal = (result?: { id?: number, name?: string, account?: string, description?: string, updated?: boolean, metadataOnly?: boolean, accountOption?: any }) => {
        reset();
        closeModal(result);
    }

    return (
        <Modal show={true} onHide={_handleCloseModal} backdrop="static" animation={false} size="lg">
            {/* // <div className="modal fade" id="exampleModal" aria-labelledby="exampleModalLabel" aria-hidden="true"> */}
            <div className="">
                <div className={`page-loader ${showPageLoader ? 'visible' : ''}`}>
                    <div className="loader"></div>
                </div>
                <div className="">
                    <Form onSubmit={handleSubmit(processData)}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="exampleModalLabel">{metadataOnly ? t('title.parentDetails') : (addRecord ? t('title.create') : t('title.update'))}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('button.close')} onClick={() => _handleCloseModal()}><img src={closeBtn} alt="" /></button>
                        </div>
                        <div className="modal-body pb-0">
                            <div className="row align-items-center">

                                {!metadataOnly && <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">{addRecord ? t('label.availableAccounts') : t('label.selectCOA')}<strong className='text-danger'>*</strong></label>
                                        <Controller
                                            render={({ field: { value, onChange, onBlur } }) => (
                                                // <AsyncSelect
                                                //     {...field}
                                                //     loadOptions={loadAccNumberOptions}
                                                //     defaultOptions={accountNumbers}
                                                // />
                                                <Select
                                                    placeholder={t('placeholder.accNumber')}
                                                    onChange={(e: any) => {
                                                        const param = {
                                                            target: {
                                                                name: 'account',
                                                                value: e
                                                            }
                                                        }
                                                        onChange(param)
                                                        setIsAccountTypeLoading(true);
                                                        fetchAccountLabel(e.value)
                                                        setAccountDetails({
                                                            label: null,
                                                            accountType: UNRESOLVED_DETAIL,
                                                            report: UNRESOLVED_DETAIL
                                                        });
                                                    }}
                                                    //onChange={onChange}
                                                    value={value}
                                                    defaultValue={value}
                                                    options={accountNumbers}
                                                    isDisabled={getValues('revrex_account_id') != null}
                                                />
                                            )}
                                            control={control}
                                            name="account"
                                        />
                                        {
                                            (errors && errors['account'] && errors['account'].message && (
                                                <Form.Text className="text-danger">
                                                    {errors['account'].message as ReactNode || ''}
                                                </Form.Text>
                                            )) || ''
                                        }
                                    </div>
                                </div>}
                                {!metadataOnly && <div className="col-sm-6">
                                    <div className="form-group mb-0">
                                        <p className="lebel  mb-0"><b>{accountDetails['label']}</b></p>
                                    </div>
                                </div>}
                                {!metadataOnly && <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">{t('label.report')}<strong className='text-danger'>*</strong></label>
                                        <input type='text' value={accountDetails['report'][0]['label']} disabled className='form-control' />
                                        {/* <Controller
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    placeholder={'Report'}
                                                    options={accountDetails['report']}
                                                    defaultValue={accountDetails['report']}
                                                />
                                            )}
                                            control={control}
                                            name="financial_statement"
                                        /> */}
                                        {/* {
                                            errors && errors['financial_statement'] && errors['financial_statement'].message && (
                                                <Form.Text className="text-danger">
                                                    {errors?.financial_statement?.message as ReactNode || ''}
                                                </Form.Text>
                                            ) || ''
                                        } */}
                                    </div>
                                </div>}
                                {metadataOnly && (watch('account')?.path || record?.path) && (
                                    <div className="col-sm-12 mb-3 text-muted">
                                        <strong>{t('label.revrexPath')}</strong> {watch('account')?.path || record.path}
                                    </div>
                                )}
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">
                                            {metadataOnly ? t('label.accountNumber') : t('label.accountType')}
                                            {!metadataOnly && <strong className='text-danger'>*</strong>}
                                        </label>
                                        {metadataOnly ? (
                                            <Controller
                                                control={control}
                                                name="account"
                                                render={({ field }) => (
                                                    <Select {...field} options={metadataAccountOptions} />
                                                )}
                                            />
                                        ) : (
                                            <input type='text' value={accountDetails['accountType'][0]['label']} disabled className='form-control' />
                                        )}
                                        {/* <Controller
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    placeholder={'Account Type'}
                                                    defaultValue={accountDetails['accountType']}
                                                    options={accountDetails['accountType']}
                                                />
                                            )}
                                            control={control}
                                            name="parenthisys_control"
                                        /> */}
                                        {/* {
                                            errors && errors['parenthisys_control'] && errors['parenthisys_control'].message && (
                                                <Form.Text className="text-danger">
                                                    {errors.parenthisys_control.message as ReactNode || ''}
                                                </Form.Text>
                                            ) || ''
                                        } */}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="">{t('label.name')}<strong className='text-danger'>*</strong></label>
                                        {metadataOnly ? (
                                            <input value={record?.name || ''} className='form-control bg-light' disabled />
                                        ) : (
                                            <input {...register("name")} placeholder={t('placeholder.name')} className='form-control' />
                                        )}
                                        {
                                            (errors && errors['name'] && errors['name'].message && (
                                                <Form.Text className="text-danger">
                                                    {errors?.name?.message}
                                                </Form.Text>
                                            )) || ''
                                        }
                                    </div>
                                </div>
                            </div>
                            {/* <div className="info-box">
                                <p>Use <strong>Checking</strong> accounts to track all your checking activity, including debit card transactions.</p>
                                <p>Each checking account your company has at a bank or other financial institution should have its own Checking type account in RevRex Online Simple Start.</p>
                            </div> */}
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label htmlFor="">{t('label.description')}</label>
                                        <Controller
                                            render={({ field }) => (
                                                <textarea
                                                    {...field}
                                                    className="form-control"
                                                    placeholder={t('placeholder.description')}
                                                />
                                            )}
                                            control={control}
                                            name="description"
                                        />
                                        {
                                            (errors && errors['description'] && errors['description'].message && (
                                                <Form.Text className="text-danger">
                                                    {errors['description'].message}
                                                </Form.Text>
                                            )) || ''
                                        }
                                        {/* <textarea name="" id="" className="form-control" placeholder="Enter Descriptions"></textarea> */}
                                    </div>
                                </div>
                                {/* <div className="col-sm-6">
                                        <div className="form-group">
                                            <label className="container-checkbox">Is sub-account
                                                <input type="checkbox" />
                                                <span className="checkmark"></span>
                                            </label>
                                            <select name="" id="" className="form-select">
                                                <option value="">Enter Parent Account</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <label htmlFor="">Choose Option</label>
                                            <select name="" id="" className="form-select">
                                                <option value="">Please Select</option>
                                            </select>
                                        </div>
                                    </div> */}
                            </div>

                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={() => _handleCloseModal()}>{t('button.close')}</button>
                            <button type="submit" className="btn btn-primary" disabled={showLoader || (!metadataOnly && (isAccountTypeLoading || !watch('account')))}>
                                {(showLoader && (<Spinner animation="border" size="sm" />)) || t('button.saveChanges')}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
            {/* </div> */}
        </Modal>
    )
}

export default ChartAccountModal;
